import getExtFromContentType from "src/helpers/fileHelpers/getExtFromContentType";
import log from "src/helpers/log";
import safeParseInt from "src/helpers/safeParseInt";
import FileMetadata from "src/types/FileMetadata";

class CommonPrefetchedData {
	url: string;
	blob: () => Promise<Blob>;
	/**
	 * @deprecated Use metadata instead
	 */
	contentType: () => Promise<string>;
	/**
	 * @deprecated Use metadata instead
	 */
	ext: () => Promise<string>;
	base64WithoutPrefix: () => Promise<string>;
	uri: () => Promise<string | undefined>;
	metadata: () => Promise<FileMetadata>;

	public readonly base64WithPrefix = async () => {
		const base64 = await this.base64WithoutPrefix();
		const contentType = await this.contentType();
		return `data:${contentType};base64,${base64}`;
	};

	protected readonly fetchDelay: number;

	constructor(url: string, args: { fetchDelay?: number; lazy?: boolean } = {}) {
		this.url = url;
		this.fetchDelay = args.fetchDelay || 0;
	}

	protected readonly delay = async () => {
		if (this.fetchDelay > 0) {
			await new Promise((resolve) => setTimeout(resolve, this.fetchDelay));
		}
	};
	protected readonly initialHeadFetch = async () => {
		const res = await fetch(this.url, { method: "HEAD" });
		if (!res.ok) throw new Error("Failed to prefetch header data");

		const contentType = res.headers.get("content-type") ?? "image/jpeg";
		const ext = getExtFromContentType(contentType);
		const contentDisposition = res.headers.get("content-disposition") ?? null;
		const contentLength = res.headers.get("content-length") ?? null;
		return {
			contentType,
			ext,
			contentDisposition,
			contentLength: safeParseInt(contentLength),
		};
	};

	protected readonly initialFetch = async () => {
		const res = await fetch(this.url);
		if (!res.ok) {
			log.error(
				{
					url: this.url,
					status: res.status,
				},
				"Failed to prefetch; fetch failed",
			);
			throw new Error(`Failed to prefetch`);
		}
		const blob = await res.blob();
		if (!blob) {
			log.error(
				{
					url: this.url,
				},
				"Failed to prefetch; missing blob",
			);
			throw new Error(`Failed to prefetch; missing blob`);
		}
		let contentType = res.headers.get("content-type") || "";
		if (contentType === "application/octet-stream") {
			contentType = "image/jpeg";
		}

		let ext = contentType.split("/")[1] ?? "jpg";
		if (ext === "jpeg") ext = "jpg";
		const contentDisposition = res.headers.get("content-disposition") ?? null;
		const contentLength = res.headers.get("content-length") ?? null;

		return {
			blob: blob,
			contentType: contentType,
			ext: ext,
			contentDisposition: contentDisposition,
			contentLength: safeParseInt(contentLength),
		};
	};

	protected readonly validate = () => {
		if (!this.url) {
			throw new Error("No url");
		}
		if (!this.blob) {
			throw new Error("No blob");
		}
		if (!this.contentType) {
			throw new Error("No content type");
		}
		if (!this.ext) {
			throw new Error("No ext");
		}
		if (!this.base64WithoutPrefix) {
			throw new Error("No base64");
		}
		if (!this.uri) {
			throw new Error("No uri");
		}
		if (!this.metadata) {
			throw new Error("No metadata");
		}
	};
}
export default CommonPrefetchedData;
